"use client";

import Link from "next/link";
import { DataTable } from "@/components/ui/data-table";
import { columns } from "./columns";
import { Button } from "@/components/ui/button";
import { CirclePlus } from "lucide-react";
import {
  SmoothHeight,
  TableSkeleton,
  useSawLoading,
} from "@/components/loading-skeletons";
import { Input } from "@/components/ui/input";
import {
  useCurrentUser,
  useCurrentOrganization,
  useMembers,
  useSubscriptions,
  usePortalReducers,
} from "@/hooks/spacetime";
import { toast } from "sonner";
import {
  DEFAULT_ERROR_TITLE,
  DEFAULT_MISSING_PERMISSION_TITLE,
  DEFAULT_ORGANIZATION_UPDATED_TITLE,
  LIBUM_DOCS_POWERON_PRO_CAPABILITIES_URL,
} from "@/lib/constants";
import { getSubscriptionUrl } from "@/services/subscription.client";
import { useRouter } from "next/navigation";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useMemo } from "react";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Member } from "@/types/member";
import { overrideMessageIf403 } from "@/utils/utils";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { startNavigationFeedback } from "@/lib/navigation-feedback";

// Match the subscriptions layout's 1024px cap. Use the available content
// width so rounding follows the actual gutters, including sidebar changes.
const TABLE_CONTAINER_CLASS =
  "sm:rounded-none xxl:rounded-none [@container_poweron-pro_(min-width:1025px)]:!rounded-lg";
const TABLE_HEADER_CLASS =
  "sm:border-t xxl:border-t [@container_poweron-pro_(min-width:1025px)]:!border-t-0";

export default function PowerOnProUsers() {
  useDocumentTitle("Users");
  const router = useRouter();
  const isMobile = useIsMobile();
  const isLargeScreen = useMediaQuery("(max-width: 920px)");
  const { user, isReady: userReady } = useCurrentUser();
  const { organization, isReady: orgReady } = useCurrentOrganization();
  const { members, isReady: membersReady } = useMembers();
  const { subscriptions } = useSubscriptions(organization);
  const { updateMember } = usePortalReducers();
  const isLoading = !userReady || !orgReady || !membersReady;
  // Derived directly (not state + effect) so warm mounts with cached rows
  // render the right thing on the very first frame — no flash.
  const hasActivePowerOnProSubscription = useMemo(
    () =>
      subscriptions.some(
        (s) =>
          s.productId === process.env.NEXT_PUBLIC_STRIPE_POWERON_PRODUCT_ID &&
          s.status === "active",
      ),
    [subscriptions],
  );
  const users = useMemo(
    () =>
      members
        .filter((m) => m.status === "active")
        .sort((a, b) =>
          a.userId === user?.id ? -1 : b.userId === user?.id ? 1 : 0,
        ),
    [members, user],
  );
  const sawLoading = useSawLoading(users.length === 0 && isLoading);

  const handleToggleSubscription = async (member: Member, checked: boolean) => {
    if (organization === null || user === null) return;

    const isAdmin = users.find((u) => u.userId === user.id)?.role === "admin";

    // Protect against non-admins and non-owners
    if (user.id !== organization.ownerId && !isAdmin) {
      toast.error(DEFAULT_MISSING_PERMISSION_TITLE, {
        description:
          "You must be the organization owner or an admin to manage PowerOn Pro+ seats",
      });
      return;
    }

    try {
      const subscription = subscriptions.find(
        (s) =>
          s.productId === process.env.NEXT_PUBLIC_STRIPE_POWERON_PRODUCT_ID &&
          s.status === "active",
      );
      const availableSeats =
        (subscription?.seats || 0) -
        members.filter((m) => m.isPowerOnProPlus).length;

      if (checked && availableSeats <= 0) {
        if (!subscription) {
          toast.error("No active subscription", {
            description:
              "You need an active subscription to enable PowerOn Pro+",
            action: {
              label: "Subscriptions",
              onClick: async () => {
                startNavigationFeedback("/settings/subscriptions");
                router.push("/settings/subscriptions");
              },
            },
          });
          return;
        }
        toast.error("No available seats", {
          description:
            "You have reached the maximum number of PowerOn Pro+ seats",
          action: {
            label: "Add seats",
            onClick: async () => {
              if (organization === null) return;

              try {
                const url = await getSubscriptionUrl(
                  organization.id,
                  organization.customerId,
                  subscription
                    ? {
                        type: "subscription_update",
                        subscriptionId: subscription.id,
                      }
                    : undefined,
                );

                if (url !== "" && subscription) {
                  window.open(url, "_self");
                } else {
                  toast.error(DEFAULT_ERROR_TITLE, {
                    description:
                      "We're unable to create a billing session. Please try again later.",
                  });
                }
              } catch (error: any) {
                toast.error(
                  ...overrideMessageIf403(error.message, [
                    DEFAULT_ERROR_TITLE,
                    {
                      description:
                        "There was a problem creating the Stripe billing session. Please try again later.",
                    },
                  ]),
                );
                console.warn(error.message);
              }
            },
          },
        });
        return;
      }

      if ((availableSeats > 0 && checked) || !checked) {
        await updateMember({
          id: member.id,
          organizationId: organization.id,
          isPowerOnProPlus: checked,
          role: undefined,
          status: undefined,
          inviteToken: undefined,
        });

        // Display message based on toggle state
        if (checked) {
          toast.success(DEFAULT_ORGANIZATION_UPDATED_TITLE, {
            description: `Added ${member.email} to PowerOn Pro+ seats`,
          });
        } else {
          toast.success(DEFAULT_ORGANIZATION_UPDATED_TITLE, {
            description: `Removed ${member.email} PowerOn Pro+ seats`,
          });
        }
      }
    } catch (error: any) {
      console.warn(error.message);
    }
  };

  const visibleColumns = columns(handleToggleSubscription).filter((c: any) =>
    isMobile || isLargeScreen
      ? ["email", "plusSubscription"].includes(c.accessorKey)
      : true,
  );

  return (
    <div className="lg:col-span-9">
      {!hasActivePowerOnProSubscription && (
        <Card className="-mt-2 mr-2.5 shadow-sm sm:-mx-1 md:mx-4">
          <CardHeader className="px-4 pb-4 pt-5 sm:p-6 md:p-8">
            <CardTitle className="text-sm sm:text-base md:text-lg">
              Subscribe to PowerOn Pro
              <sup className="font-bold text-brand-accent">+</sup>
            </CardTitle>
            <CardDescription className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-2">
              <div className="text-xs sm:text-sm md:text-base">
                Be among the greatest PowerOn developers with an
                <Link
                  href={LIBUM_DOCS_POWERON_PRO_CAPABILITIES_URL}
                  target="_blank"
                  className="mx-1 font-bold text-emerald-400"
                >
                  evolving
                </Link>
                set of powerful tools.
              </div>
              <Button
                variant="secondary"
                className="w-full shrink-0 sm:w-auto"
                onClick={() => {
                  startNavigationFeedback("/settings/subscriptions");
                  router.push("/settings/subscriptions");
                }}
              >
                View Subscriptions
              </Button>
            </CardDescription>
          </CardHeader>
        </Card>
      )}
      <div className="mt-6 flex flex-col justify-between gap-4 pl-1 pr-3.5 sm:px-5 lg:flex-row lg:items-center lg:gap-2">
        <div className="flex flex-grow items-center justify-between">
          <h2 className="mt-1.5 flex flex-row gap-2 text-xl font-semibold leading-6 text-gray-1100 dark:text-gray-100 lg:mt-0">
            Users
            <span className="gap-2 text-muted">({users.length})</span>
          </h2>
        </div>

        <div className="mt-0 flex flex-grow flex-row items-center gap-2 lg:flex-grow-0">
          <Input
            type="search"
            placeholder="Find a user"
            className="mt-0 w-full lg:w-[400px]"
            id="user-search"
          />
          <Button asChild className="w-fit">
            <Link href="/settings/members">
              <CirclePlus />
              Add Users
            </Link>
          </Button>
        </div>
      </div>
      <div className="lb-scrollbar-overlay -ml-3.5 -mr-1 overflow-x-auto py-6 sm:-mx-6">
        <SmoothHeight>
          {users.length === 0 && isLoading ? (
            <TableSkeleton
              columns={visibleColumns}
              containerClassName={TABLE_CONTAINER_CLASS}
              headerClassName={TABLE_HEADER_CLASS}
            />
          ) : (
            <TooltipProvider>
              <DataTable
                columns={visibleColumns}
                animateBody={sawLoading}
                data={users}
                searchField={["email", "name"]}
                searchInputId="user-search"
                containerClassName={TABLE_CONTAINER_CLASS}
                headerClassName={TABLE_HEADER_CLASS}
              />
            </TooltipProvider>
          )}
        </SmoothHeight>
      </div>
    </div>
  );
}
